import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from "@b2b/ui-components";
import { sellerService } from "@b2b/services";

interface EnquiryItem {
  id: string;
  subject: string;
  status: "PENDING" | "RESPONDED" | "CLOSED";
  createdAt: string;
  company?: { id: string; name: string } | null;
  product?: { id: string; name: string } | null;
}

const STATUS_STYLES: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-700 border border-yellow-400",
  RESPONDED: "bg-green-100 text-green-700 border border-green-500",
  CLOSED: "border border-gray-300 text-gray-600",
};

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export function RecentEnquiries() {
  const [enquiries, setEnquiries] = useState<EnquiryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await sellerService.getMyStorefrontEnquiries(
          undefined,
          1,
          5,
        );
        setEnquiries(
          (Array.isArray(res.inquiries) ? res.inquiries : []).slice(0, 5),
        );
      } catch (err: unknown) {
        console.error("Error fetching recent enquiries:", err);
        setError(err instanceof Error ? err.message : "Failed to load");
        setEnquiries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecent();
  }, []);

  return (
    <Card className="shadow-md h-full">
      <CardHeader className="pb-4 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Mail className="w-5 h-5 text-orange-600" />
          Recent Enquiries
        </CardTitle>
        <Button variant="ghost" size="sm" asChild className="shrink-0">
          <Link to="/buyer/enquiries" className="flex items-center gap-1">
            View all
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
            {error}
          </div>
        ) : enquiries.length === 0 ? (
          <div className="p-6 rounded-lg border border-dashed border-gray-200 text-center text-gray-500 text-sm">
            No enquiries yet. Send an enquiry from a product or shop page to
            connect with sellers.
          </div>
        ) : (
          <ul className="space-y-3">
            {enquiries.map((item) => (
              <li
                key={item.id}
                className="p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-gray-900 line-clamp-1">
                    {item.subject || item.product?.name || "Enquiry"}
                  </p>
                  <Badge
                    className={
                      STATUS_STYLES[item.status] ?? STATUS_STYLES.CLOSED
                    }
                  >
                    {item.status.charAt(0) + item.status.slice(1).toLowerCase()}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500">
                  {item.company?.name ? `${item.company.name} · ` : ""}
                  {formatDate(item.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
